import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import React from "react";
import { useHistory } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

// Pressing back from the first history entry the app was loaded on leaves the
// site, which browsers don't let us intercept. So that entry is tagged "root"
// and a duplicate "trap" entry is pushed on top of it: arriving back at root
// means the next back press would leave, and that's when we ask the user to
// log out first. Otherwise their session survives and the forward button
// takes them straight back in.
const GUARD_KEY = "__exitGuard";
type GuardMark = "root" | "trap";

const markOf = (state: unknown) =>
  (state as Record<string, unknown> | null)?.[GUARD_KEY];

const withMark = (mark: GuardMark) => ({
  ...(window.history.state ?? {}),
  [GUARD_KEY]: mark,
});

// How long to wait for history.go(-1) to leave before assuming there's no
// previous page (e.g. the app was opened in a new tab).
const LEAVE_FALLBACK_MS = 500;

const ExitGuard: React.FC = () => {
  const history = useHistory();
  const { isAuthenticated, signOut } = useAuth();
  const [open, setOpen] = React.useState(false);
  const [leaving, setLeaving] = React.useState(false);

  const isAuthenticatedRef = React.useRef(isAuthenticated);
  isAuthenticatedRef.current = isAuthenticated;
  const atRootRef = React.useRef(true);

  // Router replaces (e.g. ProtectedRoute redirecting to /signin on load)
  // overwrite the entry's state, so re-tag root when it's the one replaced.
  React.useEffect(
    () =>
      history.listen((_location, action) => {
        if (action === "PUSH") {
          atRootRef.current = false;
        } else if (action === "REPLACE" && atRootRef.current) {
          window.history.replaceState(withMark("root"), "");
        }
      }),
    [history]
  );

  React.useEffect(() => {
    window.history.replaceState(withMark("root"), "");

    // Chrome skips history entries pushed without a user gesture when the
    // back button is pressed, so the trap is only pushed on the first one.
    const arm = () => {
      window.removeEventListener("pointerdown", arm, true);
      window.removeEventListener("keydown", arm, true);
      if (markOf(window.history.state) === "root") {
        window.history.pushState(withMark("trap"), "");
        atRootRef.current = false;
      }
    };
    window.addEventListener("pointerdown", arm, true);
    window.addEventListener("keydown", arm, true);

    const onPopState = (event: PopStateEvent) => {
      atRootRef.current = markOf(event.state) === "root";
      if (!atRootRef.current) {
        setOpen(false);
        return;
      }
      if (isAuthenticatedRef.current) {
        setOpen(true);
      } else {
        window.history.back();
      }
    };
    window.addEventListener("popstate", onPopState);

    return () => {
      window.removeEventListener("pointerdown", arm, true);
      window.removeEventListener("keydown", arm, true);
      window.removeEventListener("popstate", onPopState);
    };
  }, []);

  const stay = () => {
    setOpen(false);
    window.history.forward();
  };

  const logOutAndLeave = async () => {
    setLeaving(true);
    await signOut();
    setOpen(false);
    setLeaving(false);

    const fallback = window.setTimeout(
      () => history.replace("/signin"),
      LEAVE_FALLBACK_MS
    );
    window.addEventListener("pagehide", () => window.clearTimeout(fallback), {
      once: true,
    });
    window.history.go(-1);
  };

  return (
    <Dialog
      open={open}
      onClose={leaving ? undefined : stay}
      aria-labelledby="exit-guard-title"
      aria-describedby="exit-guard-description"
    >
      <DialogTitle id="exit-guard-title">Leave HomeBuddy?</DialogTitle>
      <DialogContent>
        <DialogContentText id="exit-guard-description">
          For your security, you'll be logged out before you leave. You'll
          need to sign in again next time.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={stay} disabled={leaving} sx={{ textTransform: "none" }}>
          Stay
        </Button>
        <Button
          variant="contained"
          onClick={logOutAndLeave}
          disabled={leaving}
          sx={{
            textTransform: "none",
            backgroundColor: "var(--ltn__secondary-color)",
            "&:hover": { backgroundColor: "var(--ltn__paragraph-color)" },
          }}
        >
          {leaving ? "Logging out…" : "Log out and leave"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExitGuard;
